const sectionsToRender = [
  {
    id: '#cart-counter',
    section: 'cart-counter',
    selector: '#shopify-section-cart-counter'
  },
  {
    id: '#CartDrawer-Body',
    section: 'cart-drawer',
    selector: '#shopify-section-cart-drawer #CartDrawer-Body'
  }
];
class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.recommendationsPerformed = false;

    this.addEventListener(
      'keyup',
      event => event.code.toUpperCase() === 'ESCAPE' && this.close()
    );
    this.querySelector('#CartDrawer-Overlay').addEventListener(
      'click',
      this.close.bind(this)
    );
    this.setCartLink();
    this.setCheckoutHandlers();
    this.setGiftPreview();
  }

  connectedCallback() {
    this.toggleCheckoutButton();
  }

  performRecommendations() {
    const recommendationsHolder = this.querySelector(
      '[data-cart-drawer-upsells]'
    );
    if (!recommendationsHolder || this.recommendationsPerformed)
      return;
    this.recommendationsPerformed = true;

    fetch(recommendationsHolder.dataset.url)
      .then(response => response.text())
      .then(text => {
        const recommendations = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('[data-cart-drawer-upsells]').outerHTML;
        recommendationsHolder.outerHTML = recommendations;
      });
  }

  setCartLink() {
    const cartLink = document.querySelector('[data-cart-link]');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.setAttribute('aria-expanded', 'false');
    this.cartLink = cartLink;
    cartLink.addEventListener('click', event => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', event => {
      if (event.code.toUpperCase() !== 'SPACE') return;
      event.preventDefault();
      this.open(cartLink);
    });
  }

  onCartAdd(event) {
    event.preventDefault();

    addToCart(
      event.target,
      this.getSectionsToRender().map(section => section.section)
    )
      .then(response => {
        if (response.status) throw new Error(response.message);

        this.renderContents(response);
      })
      .catch(error => {
        console.error(error);
      });
  }

  open(opener) {
    // Opened after an add to cart there is no opener passed in, so remember
    // whatever had focus (the add to cart button) to return focus there.
    const focused = document.activeElement;
    if (opener) {
      this.setActiveElement(opener);
    } else if (focused && focused !== document.body && !this.contains(focused)) {
      this.setActiveElement(focused);
    }
    this.classList.add('is-visible');
    this.cartLink?.setAttribute('aria-expanded', 'true');

    // With reduced motion the slide-in has no transition, so `transitionend`
    // never fires and focus used to stay behind the open drawer.
    const inner = this.querySelector('.cart-drawer__inner');
    if (inner && parseFloat(getComputedStyle(inner).transitionDuration) > 0) {
      this.addEventListener(
        'transitionend',
        () => {
          this.focusOnCartDrawer();
        },
        { once: true }
      );
    } else {
      this.focusOnCartDrawer();
    }
    bodyScroll.lock(this.querySelector('#CartDrawer-Body'));
    this.performRecommendations();
  }

  close() {
    this.toggleGiftPreview(false);
    this.classList.remove('is-visible');
    this.cartLink?.setAttribute('aria-expanded', 'false');
    removeTrapFocus(this.activeElement);
    bodyScroll.unlock(this.querySelector('#CartDrawer-Body'));
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  focusOnCartDrawer() {
    const containerToTrapFocusOn = this.querySelector('#CartDrawer');
    const focusElement = this.querySelector('[data-drawer-close]');
    trapFocus(containerToTrapFocusOn, focusElement);
  }

  renderContents(response, open = true) {
    this.getSectionsToRender().forEach(section => {
      const sectionElement = document.querySelector(section.id);
      const cartPartsSelectors = [
        '[data-cart-title]',
        '[data-cart-shipping-bar]',
        '[data-cart-summary]'
      ];

      sectionElement.innerHTML = this.getSectionInnerHTML(
        response.sections[section.section],
        section.selector
      );
      this.updateCartParts(
        this.getCartElements(response, section, cartPartsSelectors),
        cartPartsSelectors
      );
    });
    this.recommendationsPerformed = false;
    if (open) this.open();
  }

  // Re-renders the drawer from the current cart without opening it.
  refresh() {
    const sections = this.getSectionsToRender()
      .map(section => section.section)
      .join(',');

    return fetch(`${window.location.pathname}?sections=${sections}`)
      .then(response => response.json())
      .then(sectionsHtml => {
        this.renderContents({ sections: sectionsHtml }, false);
      });
  }

  setCheckoutHandlers() {
    this.addEventListener('submit', event => {
      const form = event.target.closest('[data-cart-checkout]');
      if (!form || !form.dataset.protectionVariant) return;
      if (form.dataset.protectionInCart === 'true') return;

      event.preventDefault();
      this.checkout(form, true);
    });

    this.addEventListener('click', event => {
      const button = event.target.closest('[data-checkout-unprotected]');
      if (!button) return;

      const form = button.closest('[data-cart-checkout]');
      const terms = form.querySelector('[name="terms"]');
      if (terms && !terms.checked) {
        terms.reportValidity();
        return;
      }

      this.checkout(form, false);
    });
  }

  // Sets the package protection line, then goes to checkout. The fee is a
  // percentage of the order: Liquid picks the protection variant priced at
  // that fee, with quantity 1 ($4.14 -> the "4.15" variant). Any other
  // protection line from an earlier visit is replaced in the same
  // /cart/update request.
  checkout(form, protect) {
    const variantId = form.dataset.protectionVariant;
    const quantity = Number(form.dataset.protectionQuantity) || 1;
    const inCart = form.dataset.protectionInCart === 'true';
    const lines = (form.dataset.protectionLines || '').split(',').filter(Boolean);
    const error = form.querySelector('[data-checkout-error]');
    const goToCheckout = () => {
      window.location.href = form.dataset.checkoutUrl;
    };

    let request = null;
    if ((protect && !inCart) || (!protect && lines.length)) {
      const updates = {};
      lines.forEach(id => {
        updates[id] = 0;
      });
      if (protect) updates[variantId] = quantity;

      request = fetch(`${routes.cart_update_url}.js`, {
        ...fetchConfig('json'),
        body: JSON.stringify({ updates })
      });
    }

    if (!request) {
      goToCheckout();
      return;
    }

    form.classList.add('is-loading');
    request
      .then(response => {
        if (!response.ok) throw new Error(window.cartStrings?.error);
        goToCheckout();
      })
      .catch(err => {
        form.classList.remove('is-loading');
        if (error) {
          error.textContent = err.message || window.cartStrings?.error;
          error.hidden = false;
        }
      });
  }

  // Free gift image next to the reward 2 milestone. Hover shows it (CSS);
  // a click or tap on the icon or label toggles it for touch screens. The
  // rewards bar is re-rendered on cart updates, so listeners are delegated.
  setGiftPreview() {
    this.addEventListener('click', event => {
      const milestone = event.target.closest('[data-gift-preview]');
      const onTrigger = event.target.closest('[data-gift-preview-toggle], .cart-rewards__icon');
      if (milestone && onTrigger) {
        this.toggleGiftPreview(!milestone.classList.contains('is-gift-open'));
        return;
      }
      this.toggleGiftPreview(false);
    });

    this.addEventListener('keyup', event => {
      if (event.code.toUpperCase() !== 'ESCAPE') return;
      if (!this.querySelector('[data-gift-preview].is-gift-open')) return;
      // Escape closes the image first, not the whole drawer.
      event.stopImmediatePropagation();
      this.toggleGiftPreview(false);
    }, true);
  }

  toggleGiftPreview(open) {
    const milestone = this.querySelector('[data-gift-preview]');
    if (!milestone) return;
    milestone.classList.toggle('is-gift-open', open);
    milestone.querySelector('[data-gift-preview-toggle]')?.setAttribute('aria-expanded', String(open));
  }

  getSectionsToRender() {
    return sectionsToRender;
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  getCartElements(response, section, selectors) {
    return selectors.map(selector => {
      return new DOMParser()
        .parseFromString(
          response.sections[section.section],
          'text/html'
        )
        .querySelector(selector);
    });
  }

  updateCartParts(elements, selectors) {
    elements.forEach((element, index) => {
      if (!element) return;

      const elementToReplace = document.querySelector(
        selectors[index]
      );
      elementToReplace.innerHTML = element.innerHTML;
    });
  }

  toggleCheckoutButton() {
    this.addEventListener('change', event => {
      if (event.target.getAttribute('name') !== 'terms') return;

      this.querySelector('[name="checkout"]')?.classList.toggle(
        'not-allowed',
        !event.target.checked
      );
    });
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  constructor() {
    super();
    this.cartDrawer = document.querySelector('cart-drawer');
  }

  performRecommendations() {
    this.cartDrawer.recommendationsPerformed = false;
    this.cartDrawer.performRecommendations();
  }

  getSectionsToRender() {
    return sectionsToRender;
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

// Rewards bar thresholds are set in the shop currency. Liquid renders the bar
// assuming the cart uses that currency; when the customer shops in another
// one, convert the thresholds and redraw the amounts here.
class CartRewards extends HTMLElement {
  connectedCallback() {
    const currency = window.Shopify?.currency;
    const rate = Number(currency?.rate) || 1;
    if (!currency?.active || currency.active === this.dataset.shopCurrency) return;

    const total = Number(this.dataset.total);
    // Round converted thresholds to whole currency units.
    const t1 = Math.round((Number(this.dataset.t1) * rate) / 100) * 100;
    const t2 = Math.round((Number(this.dataset.t2) * rate) / 100) * 100;
    const max = t2 || t1;
    const fill = Math.min(100, (total * 100) / max);
    const m1 = (t1 * 100) / max;
    const format = cents => {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || undefined, {
          style: 'currency',
          currency: currency.active,
          // Short symbol (e.g. ৳ rather than BDT) keeps the milestone labels apart
          currencyDisplay: 'narrowSymbol',
          maximumFractionDigits: cents % 100 ? 2 : 0
        }).format(cents / 100);
      } catch (error) {
        return `${(cents / 100).toFixed(2)} ${currency.active}`;
      }
    };

    this.style.setProperty('--rewards-fill', `${fill}%`);
    this.style.setProperty('--rewards-m1', `${m1}%`);

    // Each threshold is printed twice: above the icon and, on mobile, in the label.
    this.querySelectorAll('[data-rewards-price="1"]').forEach(el => { el.textContent = format(t1); });
    this.querySelectorAll('[data-rewards-price="2"]').forEach(el => { el.textContent = format(t2); });
    const milestones = this.querySelectorAll('[data-rewards-milestone]');
    milestones[0]?.classList.toggle('is-reached', total >= t1);
    milestones[1]?.classList.toggle('is-reached', total >= t2);

    const tag = this.querySelector('[data-rewards-tag]');
    if (tag) {
      tag.textContent = format(total);
      tag.hidden = total <= 0 || fill >= 88 || Math.abs(fill - m1) < 14;
    }

    let remaining = 0;
    let reward = '';
    if (total < t1) {
      remaining = t1 - total;
      reward = this.getAttribute('data-label-1');
    } else if (t2 && total < t2) {
      remaining = t2 - total;
      reward = this.getAttribute('data-label-2');
    }

    const message = this.querySelector('[data-rewards-message]');
    if (!message) return;
    message.textContent = '';
    if (!remaining) {
      message.textContent = this.dataset.successText;
      return;
    }
    const [before, after = ''] = this.dataset.progressText.split('[amount]');
    const amount = document.createElement('strong');
    amount.textContent = format(remaining);
    message.append(
      before.replace('[reward]', reward),
      amount,
      after.replace('[reward]', reward)
    );
  }
}

if (!customElements.get('cart-rewards')) {
  customElements.define('cart-rewards', CartRewards);
}
